import { createTheme, Theme, alpha, getContrastRatio } from "@mui/material/styles";
import { colours } from "./shared/colors";

const palette = {
    tertiary: {
        main: colours.primary.main,
        light: alpha(colours.primary.main, 0.5),
        dark: alpha(colours.primary.main, 0.9),
        contrastText: getContrastRatio(colours.primary.main, '#fff') > 4.5 ? '#fff' : colours.darkGrey.main,
    },
};

// Augment the palette to include a violet color
declare module '@mui/material/styles' {
    interface Palette {
      tertiary: Palette['primary'];
    }
  
    interface PaletteOptions {
      tertiary?: PaletteOptions['primary'];
    }
  }

declare module "@mui/material/styles" {
	interface TypographyVariants {
		cardHeader: React.CSSProperties;
		pageHeader: React.CSSProperties;
		quoteTabHeader: React.CSSProperties;
		cardContentHeader: React.CSSProperties;
		basePremiumHeader: React.CSSProperties;
		cardSubtitle1: React.CSSProperties;
		cardSubtitle2: React.CSSProperties;
	}

	// allow configuration using `createTheme`
	interface TypographyVariantsOptions {
		cardHeader?: React.CSSProperties;
		pageHeader?: React.CSSProperties;
		quoteTabHeader?: React.CSSProperties;
		cardContentHeader?: React.CSSProperties;
		basePremiumHeader?: React.CSSProperties;
		cardSubtitle1?: React.CSSProperties;
		cardSubtitle2?: React.CSSProperties;
	}

	interface BreakpointOverrides {
		loginImage: true;
	}
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
	interface TypographyPropsVariantOverrides {
		cardHeader: true;
		pageHeader: true;
		quoteTabHeader: true;
		cardContentHeader: true;
		basePremiumHeader: true;
		cardSubtitle1: true;
		cardSubtitle2: true;
	}
}

declare module "@mui/material/Button" {
	interface ButtonPropsVariantOverrides {
		greenFilled: true;
        containedTertiary: true;
	}
    interface ButtonPropsColorOverrides {
        tertiary: true;
    }
}

const theme = createTheme({
	breakpoints: {
		values: {
			xs: 0,
			sm: 600,
			md: 900,
			lg: 1200,
			xl: 1536,
			loginImage: 915,
		},
	},
	palette: {
		primary: {
			main: colours.primaryDark.main,
		},
        tertiary: palette.tertiary,
	},
	typography: {
		fontFamily: "Poppins",
		cardHeader: {
			fontSize: "24px",
			fontFamily: '"Poppins", sans-serif',
			fontWeight: 600,
			paddingLeft: "8px",
			color: colours.darkGrey.main,
		},
		pageHeader: {
			margin: 0,
			fontFamily: '"Poppins", sans-serif',
			fontSize: "28px",
			color: colours.darkGrey.main,
		},
		quoteTabHeader: {
			fontFamily: "Poppins-Bold",
			fontSize: "16px",
			color: colours.darkGrey.main,
		},
		cardContentHeader: {
			fontFamily: '"Poppins", sans-serif',
			fontWeight: 600,
			fontSize: "16px",
			color: colours.darkGrey.main,
		},
		basePremiumHeader: {
			fontSize: "14px",
			fontFamily: '"Poppins", sans-serif',
			fontWeight: 600,
			color: "#989898",
		},
        cardSubtitle1: {
            fontSize: "14px",
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 600,
            color: colours.darkGrey.main,
        },
        cardSubtitle2: {
            fontSize: "14px",
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 500,
            color: colours.darkGrey.main,
        }
	},
	components: {
		MuiTypography: {
			defaultProps: {
				variantMapping: {
					cardHeader: "h3",
				},
			},
			styleOverrides: {
				subtitle1: {
					fontFamily: '"Poppins", sans-serif',
					fontWeight: 400,
					fontSize: "15px",
					color: colours.darkGrey.main,
				},
			},
		},
		MuiFormControl: {
			styleOverrides: {
				root: {
					backgroundColor: "rgba(0,0,0,0)",
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					backgroundColor: "#f5f5f5",
					// borderRadius: '5px',
					// '& .Mui-disabled': {
					// 	backgroundColor: 'rgba(224, 224, 224, 1)',
					// }
				},
				input: {
					backgroundColor: "#f5f5f5",
				},
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					backgroundColor: "rgba(0,0,0,0)",
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: "none",
					fontFamily: '"Poppins", sans-serif',
					fontWeight: 600,
					fontSize: 15,
					borderRadius: 20,
				},
				text: {
					// color: "#6fb757",
				},
				contained: {
					background: colours.primaryDark.main,
					color: "#ffffff",
					padding: "5px 30px",
					margin: "2px 5px",
					"&:disabled": {
						background: "rgba(182, 182, 182, 1)",
					},
				},
				containedPrimary: {
					background: colours.primary.main,
					color: "#ffffff",
					"&:hover": {
						background: colours.primary.hover,
					},
					"&:disabled": {
						background: "rgba(182, 182, 182, 1)",
					},
				},
				containedSecondary: {
					background: colours.darkGrey.main,
					color: "#ffffff",
					"&:hover": {
						background: colours.darkGrey.hover,
					},
					"&:disabled": {
						background: "rgba(182, 182, 182, 1)",
					},
				},
                // containedTertiary: {
				// 	background: colours.primaryDark.main,
				// 	color: "#ffffff",
				// 	"&:hover": {
				// 		background: colours.primaryDark.hover,
				// 	},
				// 	"&:disabled": {
				// 		background: "rgba(182, 182, 182, 1)",
				// 	},
				// },
                textPrimary: {
                    color: colours.primaryDark.main,
                },
				textSecondary: {
					color: colours.darkGrey.main,
					// "&:hover": {
					// 	background: colours.darkGrey.hover,
					// },
					// "&:disabled": {
					// 	color: "rgba(182, 182, 182, 1)!important",
					// },
				},
			},
			variants: [
				{
					props: { variant: "greenFilled" },
					style: ({ theme }: { theme: Theme }) => ({
						// ...theme.components?.MuiButton?.styleOverrides?.contained,
						backgroundColor: colours.green.main,
						color: "#ffffff",
						// color: '#505AFC',
						// fontWeight: 'bold',
						boxShadow: theme.shadows[4],
						"&:hover": {
							backgroundColor: colours.green.hover,
							boxShadow: theme.shadows[6],
						},
					}),
				},
                {
                    props: { variant: 'containedTertiary' },
                    style: ({ theme }: { theme: Theme }) => ({
                            background: colours.primaryDark.main,
                            color: "#ffffff",
                            "&:hover": {
                                background: colours.primaryDark.hover,
                            },
                            "&:disabled": {
                                background: "rgba(182, 182, 182, 1)",
                            },
                        }),
                },
				// {
				// 	props: { variant: 'darkOrangeFilled' },
				// 	style: ({ theme }: { theme: Theme }) => ({
				//         // ...theme.components?.MuiButton?.styleOverrides?.contained,
				// 		backgroundColor: colours.primaryDark.main,
				//         color: "#ffffff",
				// 		// color: '#505AFC',
				// 		// fontWeight: 'bold',
				// 		boxShadow: theme.shadows[4],
				// 		'&:hover': {
				// 			backgroundColor: colours.green.hover,
				// 			boxShadow: theme.shadows[6],
				// 		},
				// 	}),
				// },
			],
		},
		MuiTabs: {
			styleOverrides: {
				flexContainer: {
					button: {
						fontFamily: '"Poppins", sans-serif',
						fontSize: "16px",
						color: "#393939",
						textTransform: "none",
						letterSpacing: "0",
                        // "& .MuiSelected": {
                        //     color: "#393939 !important"
                        // },
                        // "& .MuiTab": {
                        //     color: "#393939 !important"
                        // }
					},
				},
                indicator: {
                    backgroundColor: colours.primaryLight.main,
                },
			},
		},
        MuiTab: {
            styleOverrides: {
                root: {
                    "&.Mui-selected": {
                        color: "#393939",
                    },
                }
            },
        },
		MuiBreadcrumbs: {
			styleOverrides: {
				ol: {
					"& .MuiBreadcrumbs-separator": {
						fontFamily: '"Poppins", sans-serif',
						color: "#a5a4a4",
						fontSize: "12px",
					},
					"& .MuiBreadcrumbs-li": {
						fontFamily: '"Poppins", sans-serif',
						color: "#a5a4a4",
						fontSize: "16px",
						"& a": {
							textDecoration: "none",
							color: "#a5a4a4",
						},
						"& .MuiTypography-subtitle1": {
							fontFamily: '"Poppins", sans-serif',
							color: "#a5a4a4",
							fontSize: "16px",
						},
					},
				},
			},
		},
		MuiStepIcon: {
			styleOverrides: {
				text: {
					fill: "#ffffff",
				},
                root: {
                    "&.Mui-active": { color: colours.primary.main },
                    "&.Mui-completed": { color: colours.primary.main }
                }
			},
		},
	},
});

export default theme;
